import React, { useMemo, useState } from "react";
import Table from "../../../core/common/dataTable/index";

type SyncLog = {
  id: string;
  timestamp: string;
  cycleId: string;
  empNo: string;
  employeeName: string;
  sapAction: string;
  status: "Success" | "Failed" | "Pending";
  errorMessage?: string;
};

interface TableColumn<T> {
  title: string;
  key: string;
  className?: string;
  render?: (value: any, record: T, index: number) => React.ReactNode;
}

/* ================= COMPONENTS ================= */
const DashboardTile = ({
  title,
  icon,
  count,
  link,
  colorClass = "text-primary",
}: {
  title: string;
  icon?: string;
  count?: string | number;
  link: string;
  colorClass?: string;
}) => {
  return (
    <div className="col-xxl-3 col-xl-3 col-lg-3 col-md-6 col-sm-6 mb-3">
      <div
        className="card h-100 border-0 shadow-sm hover-card"
        style={{
          borderRadius: "16px",
          transition: "all 0.3s ease",
          cursor: "pointer",
          overflow: "hidden",
          backgroundColor: "#fff"
        }}
        onClick={() => { if (link !== "#") window.location.href = link; }}
      >
        <div className="card-body d-flex align-items-center p-3">
          <div
            className={`d-flex align-items-center justify-content-center me-3 rounded-circle`}
            style={{
              width: "48px",
              height: "48px",
              backgroundColor: "rgba(var(--bs-primary-rgb), 0.03)",
              border: "1px solid rgba(0,0,0,0.03)"
            }}
          >
            {icon && <i className={`ti ${icon} ${colorClass}`} style={{ fontSize: "24px" }}></i>}
          </div>
          <div>
            <div className="text-muted small mb-1" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>{title}</div>
            <div className={`h4 fw-bold mb-0 ${colorClass}`}>{count}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardSection = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="mb-4">
      <h5 className="fw-bold text-secondary mb-3 ms-1" style={{ fontSize: "15px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {title}
      </h5>
      <div className="row">{children}</div>
    </div>
  );
};

const SapSyncLog: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCycle, setSelectedCycle] = useState("All Cycles");
  const [selectedStatus, setSelectedStatus] = useState("All Status");

  const [logs] = useState<SyncLog[]>([
    { id: "1", timestamp: "2024-10-16 14:30:22", cycleId: "PC2024-015", empNo: "10245", employeeName: "Rajesh Kumar", sapAction: "Promotion Update", status: "Success", errorMessage: "" },
    { id: "2", timestamp: "2024-10-16 14:31:10", cycleId: "PC2025-001", empNo: "10246", employeeName: "Divya Sharma", sapAction: "Promotion Create", status: "Failed", errorMessage: "SAP timeout" },
    { id: "3", timestamp: "2024-10-17 09:12:05", cycleId: "PC2025-002", empNo: "10247", employeeName: "Amit Verma", sapAction: "Promotion Update", status: "Pending", errorMessage: "" },
  ]);

  const stats = useMemo(() => ({
    total: logs.length,
    success: logs.filter(l => l.status === 'Success').length,
    failed: logs.filter(l => l.status === 'Failed').length,
    pending: logs.filter(l => l.status === 'Pending').length,
  }), [logs]);

  const cycles = useMemo(() => {
    const unique = Array.from(new Set(logs.map(l => l.cycleId))).sort();
    return ["All Cycles", ...unique];
  }, [logs]);

  const filteredData = useMemo(() => {
    return logs.filter(l => {
      const matchesSearch = l.employeeName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.empNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        l.cycleId.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCycle = selectedCycle === "All Cycles" || l.cycleId === selectedCycle;
      const matchesStatus = selectedStatus === "All Status" || l.status === selectedStatus;
      return matchesSearch && matchesCycle && matchesStatus;
    });
  }, [logs, searchTerm, selectedCycle, selectedStatus]);

  const getStatusBadgeClass = (status: SyncLog['status']) => {
    switch (status) {
      case "Success": return "badge bg-success";
      case "Failed": return "badge bg-danger";
      case "Pending": return "badge bg-warning text-dark";
      default: return "badge bg-light text-dark border";
    }
  };

  const getValueByPath = (obj: any, path: string) => {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  };

  /* ---------- CONFIGURATIONS ---------- */
  const dashboardSections = [
    {
      title: "Audit Overview",
      tiles: [
        { title: "Total Syncs", icon: "ti-refresh", count: stats.total, link: "#", colorClass: "text-primary" },
        { title: "Successful", icon: "ti-check", count: stats.success, link: "#", colorClass: "text-success" },
        { title: "Failed", icon: "ti-alert-circle", count: stats.failed, link: "#", colorClass: "text-danger" },
        { title: "Pending", icon: "ti-clock", count: stats.pending, link: "#", colorClass: "text-warning" },
      ]
    }
  ];

  const columns: TableColumn<SyncLog>[] = [
    { title: "Timestamp", key: "timestamp", className: "text-muted" },
    { title: "Cycle ID", key: "cycleId", render: (val: string) => <span className="fw-medium text-dark">{val}</span> },
    {
      title: "Employee", key: "employee", render: (_: any, record: SyncLog) => (
        <>
          <div className="fw-medium text-dark">{record.employeeName}</div>
          <small className="text-muted">{record.empNo}</small>
        </>
      )
    },
    { title: "SAP Action", key: "sapAction" },
    {
      title: "Status",
      key: "status",
      className: "text-center",
      render: (s: SyncLog['status']) => <span className={getStatusBadgeClass(s)}>{s}</span>,
    },
    { title: "Error Message", key: "errorMessage", className: "text-muted small" },
    {
      title: "Actions",
      key: "actions",
      className: "text-end",
      render: (_: any, record: SyncLog) => (
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-icon btn-sm btn-outline-primary" title="Details"><i className="ti ti-eye"></i></button>
          {record.status === "Failed" && (
            <button className="btn btn-icon btn-sm btn-outline-danger" title="Retry Sync"><i className="ti ti-refresh"></i></button>
          )}
        </div>
      )
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">SAP Sync Audit Logs</h3>
            <p className="text-muted small mb-0">Monitor SAP synchronization status and errors</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-light">
              <i className="ti ti-refresh me-1"></i>
              Retry Failed (1)
            </button>
            <button className="btn btn-light">
              <i className="ti ti-download me-1"></i>
              Export Logs
            </button>
          </div>
        </div>

        {/* DASHBOARD SECTIONS */}
        {dashboardSections.map((section, sIdx) => (
          <DashboardSection key={sIdx} title={section.title}>
            {section.tiles.map((tile, tIdx) => (
              <DashboardTile
                key={tIdx}
                title={tile.title}
                icon={tile.icon}
                count={tile.count}
                link={tile.link}
                colorClass={tile.colorClass}
              />
            ))}
          </DashboardSection>
        ))}

        <div className="card">
          <div className="card-body">
            <div className="mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2">
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search logs..."
                className="form-control form-control-sm"
                style={{ maxWidth: 360 }}
              />
              <div className="d-flex gap-2">
                <select className="form-select form-select-sm" value={selectedCycle} onChange={e => setSelectedCycle(e.target.value)}>
                  {cycles.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select className="form-select form-select-sm" value={selectedStatus} onChange={e => setSelectedStatus(e.target.value)}>
                  {["All Status", "Success", "Failed", "Pending"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-hover table-center mb-0">
                <thead className="thead-light">
                  <tr>
                    {columns.map((col, idx) => (
                      <th key={idx} className={col.className || ""}>{col.title}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((record, idx) => (
                    <tr key={idx}>
                      {columns.map((col, cIdx) => (
                        <td key={cIdx} className={col.className || ""}>
                          {col.render ? col.render(getValueByPath(record, col.key), record, idx) : getValueByPath(record, col.key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {filteredData.length === 0 && (
                    <tr><td colSpan={columns.length} className="text-center py-4 text-muted">No audit logs found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <style>{`
        .btn-icon {
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          border-radius: 6px;
        }
        .hover-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.08) !important;
        }
      `}</style>
      </div>
    </div>
  );
};

export default SapSyncLog;
